'use client';
import React, { useState, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { BackgroundColorContainer } from '@web/atoms';
import { useReporter } from '../../reporter';
import { releaseModalScrollLock } from './releaseModalScrollLock';
import { StoryCard } from './StoryCard';
import { StoryCardsContent } from './StoryCardsContent';
import { StoryCardsCarousel } from './StoryCardsCarousel';
import { StoryCardModal } from './StoryCardModal';
import { renderStoryCardsCta } from './renderStoryCardsCta';
import { storyCardClicked, storyCardModalOpened } from './events';
import {
  StyledStoryCardsSection,
  LayoutGrid,
  ContentColumn,
  CardsColumn,
  MobileCtaContainer,
} from './styles.js';

export const StoryCards = ({
  starRating,
  headline,
  typography = 'heading1',
  fontColor = 'text.primary',
  subheadline,
  cta,
  layoutDirection = 'content-right',
  backgroundColor = 'background.lightest',
  cards,
}) => {
  const reporter = useReporter();
  const [activeCardIndex, setActiveCardIndex] = useState(null);
  const [modalCard, setModalCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const cardTriggerRefs = useRef([]);
  const closingTriggerRef = useRef(null);
  const isModalOpenRef = useRef(false);
  const cardsRef = useRef(cards);
  cardsRef.current = cards;

  const desktopCtaNode = useMemo(() => renderStoryCardsCta(cta), [cta]);
  const mobileCtaNode = useMemo(
    () => renderStoryCardsCta(cta, { alignmentOverride: 'center' }),
    [cta],
  );

  const handleOpenModal = useCallback(
    index => {
      const card = cardsRef.current[index];
      if (!card) {
        return;
      }

      closingTriggerRef.current = null;
      cardTriggerRefs.current[index] = document.activeElement;
      setActiveCardIndex(index);
      setModalCard(card);
      isModalOpenRef.current = true;
      setIsModalOpen(true);
      reporter.tag(
        storyCardClicked({
          quote: card.quote,
          attribution: card.attribution,
          action: 'open_modal',
        }),
      );
      reporter.tag(
        storyCardModalOpened({
          quote: card.quote,
          attribution: card.attribution,
          mediaType: card.mediaType,
        }),
      );
    },
    [reporter],
  );

  const handleCloseModal = useCallback(() => {
    closingTriggerRef.current = cardTriggerRefs.current[activeCardIndex];
    isModalOpenRef.current = false;
    setIsModalOpen(false);
    setActiveCardIndex(null);
  }, [activeCardIndex]);

  const handleModalAfterClose = useCallback(() => {
    if (isModalOpenRef.current) {
      return;
    }

    releaseModalScrollLock();

    closingTriggerRef.current?.focus?.();
    closingTriggerRef.current = null;
    setModalCard(null);
  }, []);

  return (
    <BackgroundColorContainer $bgColor={backgroundColor}>
      <StyledStoryCardsSection>
        <LayoutGrid $layoutDirection={layoutDirection}>
          <ContentColumn $layoutDirection={layoutDirection}>
            <StoryCardsContent
              starRating={starRating}
              headline={headline}
              typography={typography}
              fontColor={fontColor}
              subheadline={subheadline}
              ctaNode={desktopCtaNode}
              backgroundColor={backgroundColor}
            />
          </ContentColumn>

          <CardsColumn $layoutDirection={layoutDirection}>
            <StoryCardsCarousel cardCount={cards.length}>
              {cards.map((card, index) => (
                <StoryCard
                  key={`${card.attribution}-${card.quote}-${index}`}
                  {...card}
                  cardIndex={index}
                  onOpenModal={handleOpenModal}
                />
              ))}
            </StoryCardsCarousel>
          </CardsColumn>
        </LayoutGrid>

        {mobileCtaNode && (
          <MobileCtaContainer>{mobileCtaNode}</MobileCtaContainer>
        )}

        <StoryCardModal
          activeCard={modalCard}
          shown={isModalOpen}
          onClose={handleCloseModal}
          onAfterClose={handleModalAfterClose}
          parentHeadline={headline}
        />
      </StyledStoryCardsSection>
    </BackgroundColorContainer>
  );
};

StoryCards.displayName = 'StoryCards';

StoryCards.propTypes = {
  starRating: PropTypes.shape({
    showStars: PropTypes.bool,
    reviewCountLabel: PropTypes.string,
  }),
  headline: PropTypes.string.isRequired,
  typography: PropTypes.string,
  fontColor: PropTypes.string,
  subheadline: PropTypes.node,
  cta: PropTypes.shape({
    component: PropTypes.string,
    ctas: PropTypes.array,
    buttonsPerRow: PropTypes.number,
    href: PropTypes.string,
    children: PropTypes.node,
    target: PropTypes.string,
    text: PropTypes.string,
    variant: PropTypes.string,
    headerText: PropTypes.string,
    triggerModal: PropTypes.bool,
    modal: PropTypes.object,
    openNewTab: PropTypes.bool,
    alignment: PropTypes.oneOf(['left', 'center', 'right']),
    icon: PropTypes.object,
    iconAlignment: PropTypes.oneOf(['left', 'right']),
  }),
  layoutDirection: PropTypes.oneOf(['content-right', 'content-left']),
  backgroundColor: PropTypes.string,
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
};
